import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { videoSeekTarget, videoTapAction, videoZone, type VideoZone } from '../lib/videoGestures';

type Props = { src: string; poster?: string; startTime?: number; onTime: (time: number) => void };
export function FullscreenVideo({ src, poster, startTime, onTime }: Props) {
  const { t } = useTranslation('chatView');
  const video = useRef<HTMLVideoElement>(null);
  const container = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [boost, setBoost] = useState(false);
  const [muted, setMuted] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const press = useRef<{ id: number; x: number; y: number; moved: boolean } | null>(null);
  const previous = useRef<{ zone: VideoZone; at: number } | null>(null);
  const savedRate = useRef<number | null>(null);
  const reset = () => {
    clearTimeout(timer.current);
    if (video.current && savedRate.current !== null) video.current.playbackRate = savedRate.current;
    savedRate.current = null;
    press.current = null;
    setBoost(false);
  };
  useEffect(() => {
    const element = video.current;
    const cancel = () => reset();
    window.addEventListener('blur', cancel);
    document.addEventListener('visibilitychange', cancel);
    return () => {
      clearTimeout(timer.current);
      if (element && savedRate.current !== null) element.playbackRate = savedRate.current;
      window.removeEventListener('blur', cancel);
      document.removeEventListener('visibilitychange', cancel);
    };
  }, []);
  const toggle = () => {
    const v = video.current;
    if (v) { if (v.paused) void v.play().catch(() => {}); else v.pause(); }
  };
  const finish = (e: PointerEvent<HTMLDivElement>) => {
    const p = press.current;
    if (!p || p.id !== e.pointerId) return;
    const held = savedRate.current !== null;
    reset();
    if (held || p.moved) { previous.current = null; return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const zone = videoZone(e.clientX - rect.left, rect.width);
    const at = Date.now();
    const action = videoTapAction(zone, previous.current, at);
    previous.current = action === null ? { zone, at } : null;
    if (action === 'toggle') toggle();
    else if (action !== null && video.current) video.current.currentTime = videoSeekTarget(video.current.currentTime, action, video.current.duration);
  };
  return <div ref={container} className="fullscreen-video" onClick={e => e.stopPropagation()}>
    <div className="fullscreen-video-surface"
      onContextMenu={e => e.preventDefault()}
      onPointerDown={e => {
        if (!e.isPrimary || e.button !== 0) { reset(); return; }
        press.current = { id: e.pointerId, x: e.clientX, y: e.clientY, moved: false };
        e.currentTarget.setPointerCapture(e.pointerId);
        timer.current = setTimeout(() => {
          if (!press.current || press.current.moved || !video.current) return;
          savedRate.current = video.current.playbackRate;
          video.current.playbackRate = 2;
          previous.current = null;
          setBoost(true);
        }, 500);
      }}
      onPointerMove={e => {
        const p = press.current;
        if (p && Math.hypot(e.clientX - p.x, e.clientY - p.y) > 12) {
          p.moved = true;
          clearTimeout(timer.current);
          if (savedRate.current !== null) reset();
        }
      }}
      onPointerUp={finish} onPointerCancel={reset} onLostPointerCapture={reset}>
      <video ref={video} src={src} poster={poster} autoPlay playsInline
        onLoadedMetadata={e => {
          if (startTime) e.currentTarget.currentTime = videoSeekTarget(startTime, 0, e.currentTarget.duration);
          setDuration(Number.isFinite(e.currentTarget.duration) ? e.currentTarget.duration : 0);
        }}
        onTimeUpdate={e => { setTime(e.currentTarget.currentTime); onTime(e.currentTarget.currentTime); }}
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} />
      {boost && <span className="fullscreen-video-boost">2×</span>}
    </div>
    <div className="fullscreen-video-controls" onKeyDown={e => { if (e.key !== 'Escape') e.stopPropagation(); }} onPointerDown={e => e.stopPropagation()} onPointerUp={e => e.stopPropagation()}>
      <button type="button" onClick={toggle} aria-label={t(playing ? 'media.pause' : 'media.play')}>{playing ? 'Ⅱ' : '▶'}</button>
      <button type="button" aria-label={t('media.videoMute')} aria-pressed={muted} onClick={() => {
        if (video.current) { video.current.muted = !video.current.muted; setMuted(video.current.muted); }
      }}>{muted ? '🔇' : '🔊'}</button>
      <input type="range" min={0} max={duration || 0} step={0.1} value={Math.min(time, duration)} aria-label={t('media.videoSeek')}
        onChange={e => { if (video.current) video.current.currentTime = Number(e.target.value); }} />
      <button type="button" aria-label={t('media.videoFullscreen')} onClick={() => {
        if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
        else void container.current?.requestFullscreen?.().catch(() => {});
      }}>⛶</button>
    </div>
  </div>;
}
